import { useEffect, useState } from 'react';
import { companyThemeApi } from '../../services/api';
import type { CompanyTheme } from './companyTheme';

/**
 * The contracting company's theme for a contract preview. The company uses
 * its own theme (also before a draft exists); everyone else reads it through
 * the contract. Returns null while loading or on failure, which renders
 * Etappio's standard with the company-name mark.
 */
export function useCompanyTheme(source: { own: true } | { contractId: string } | null) {
  const [theme, setTheme] = useState<CompanyTheme | null>(null);
  const key = !source ? null : 'own' in source ? 'own' : `contract:${source.contractId}`;

  useEffect(() => {
    setTheme(null);
    if (!key) return undefined;
    let cancelled = false;
    // The theme is cosmetic: any failure (also a synchronous one) falls back
    // to Etappio's standard instead of breaking the preview.
    Promise.resolve()
      .then(() =>
        key === 'own'
          ? companyThemeApi.forCompany().get()
          : companyThemeApi.forContract(key.slice('contract:'.length))
      )
      .then(result => {
        if (!cancelled) setTheme(result && typeof result.headingColor === 'string' ? result : null);
      })
      .catch(() => { if (!cancelled) setTheme(null); });
    return () => { cancelled = true; };
  }, [key]);

  return theme;
}
